import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DateTimePicker from "@mui/lab/DateTimePicker";
import moment from "moment";
import Swal from "sweetalert2";
import api from "../../../components/services/api";

// ลำดับเดียวกับรอบจริง: ออกขาไป -> ปลายทาง -> ออกขากลับ -> ต้นทาง -> รอบถัดไป
const HOUR_FIELDS = [
  { name: "InboundTransitTime", label: "Transit Time ขาไป (ชม.)", helper: "From → To" },
  { name: "InboundOperationTime", label: "Operation Time ที่ปลายทาง (ชม.)", helper: "รอที่ To ก่อนออกขากลับ" },
  { name: "OutboundTransitTime", label: "Transit Time ขากลับ (ชม.)", helper: "To → From" },
  { name: "OutboundOperationTime", label: "Operation Time ที่ต้นทาง (ชม.)", helper: "รอที่ From ก่อนออกรอบถัดไป" },
];

const EMPTY_FORM = {
  dateFrom: null,
  dateTo: null,
  locationFrom: null,
  locationTo: null,
  InboundTransitTime: "",
  InboundOperationTime: "",
  OutboundTransitTime: "",
  OutboundOperationTime: "",
};

const fieldStyle = { width: "100%", marginTop: "10px", marginBottom: "10px" };

function locationLabel(location) {
  if (!location) return "";
  return location.VoyCode ? `${location.LocationName} (${location.VoyCode})` : location.LocationName;
}

// รูปแบบ VOY ที่จะได้: VoyCode From + To + 0 + ปี 2 หลัก + running (ระบบออกเลขตอนบันทึก)
function voyPreview(form) {
  const { locationFrom, locationTo, dateFrom } = form;
  if (!locationFrom || !locationTo) return "";
  if (!locationFrom.VoyCode || !locationTo.VoyCode) return "Location ที่เลือกยังไม่ได้กำหนด Voy Code";
  const year = moment(dateFrom ?? new Date()).format("YY");
  return `VOY ขาไป ${locationFrom.VoyCode}${locationTo.VoyCode}0${year}xxx / ขากลับ ${locationTo.VoyCode}${locationFrom.VoyCode}0${year}xxx`;
}

function toDateParam(value) {
  return value ? moment(value).format("YYYY-MM-DD HH:mm") : null;
}

function createBody(form) {
  return {
    StartDate: toDateParam(form.dateFrom),
    EndDate: toDateParam(form.dateTo),
    LocationFromId: form.locationFrom?.LocationId,
    LocationToId: form.locationTo?.LocationId,
    InboundTransitTime: form.InboundTransitTime,
    InboundOperationTime: form.InboundOperationTime,
    OutboundTransitTime: form.OutboundTransitTime,
    OutboundOperationTime: form.OutboundOperationTime,
  };
}

function createdSummary(result) {
  const rounds = result?.Rounds ?? [];
  if (rounds.length === 0) return "ไม่มีรอบในช่วงวันที่ที่เลือก";
  const first = rounds[0];
  const last = rounds.at(-1);
  return `สร้าง ${rounds.length} รอบ<br/>ขาไป ${first.VoyageIn} – ${last.VoyageIn}<br/>ขากลับ ${first.VoyageOut} – ${last.VoyageOut}`;
}

export default function DialogAddTruckSchedule({ open, onClose, onCreated }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("api/master/location")
      .then((res) => setLocations(res?.data?.data ?? []))
      .catch((e) => console.log(e));
  }, []);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY_FORM);
    setError("");
    onClose();
  };

  const handleSave = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("api/truck/create", createBody(form));
      setForm(EMPTY_FORM);
      onClose();
      onCreated();
      Swal.fire({ icon: "success", title: "สำเร็จ!", html: createdSummary(res?.data) });
    } catch (err) {
      // แสดงใน dialog เพราะ Swal จะไปอยู่หลัง MUI Dialog
      setError(err?.response?.data?.message || "บันทึกไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  };

  const preview = voyPreview(form);

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography className="textcolordarkblue" variant="h5" component="span">
          Create Truck Schedule
        </Typography>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Date From"
                inputFormat="dd MMM yyyy HH:mm"
                value={form.dateFrom}
                onChange={(value) => setField("dateFrom", value)}
                renderInput={(params) => <TextField {...params} style={fieldStyle} helperText="ETD ขาไปของรอบแรก" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Date To"
                inputFormat="dd MMM yyyy HH:mm"
                value={form.dateTo}
                minDateTime={form.dateFrom ?? undefined}
                onChange={(value) => setField("dateTo", value)}
                renderInput={(params) => <TextField {...params} style={fieldStyle} helperText="สร้างทุกรอบที่ออกขาไปก่อนเวลานี้" />}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={locations}
              value={form.locationFrom}
              getOptionLabel={locationLabel}
              isOptionEqualToValue={(option, value) => option.LocationId === value.LocationId}
              onChange={(_e, value) => setField("locationFrom", value)}
              renderInput={(params) => <TextField {...params} label="Location From" style={fieldStyle} />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={locations}
              value={form.locationTo}
              getOptionLabel={locationLabel}
              isOptionEqualToValue={(option, value) => option.LocationId === value.LocationId}
              onChange={(_e, value) => setField("locationTo", value)}
              renderInput={(params) => <TextField {...params} label="Location To" style={fieldStyle} />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {HOUR_FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.name}>
              <TextField
                name={field.name}
                label={field.label}
                helperText={field.helper}
                type="number"
                inputProps={{ min: 0, step: 0.5 }}
                InputLabelProps={{ shrink: true }}
                value={form[field.name]}
                onChange={(e) => setField(field.name, e.target.value)}
                style={fieldStyle}
              />
            </Grid>
          ))}
        </Grid>

        {preview !== "" && (
          <Typography variant="body2" className="textcolorgreen" style={{ marginTop: 10 }}>
            {preview}
          </Typography>
        )}
        {error !== "" && (
          <Alert severity="error" style={{ marginTop: 10 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          type="button"
          variant="outlined"
          sx={{ width: 80 }}
          style={{ color: "#80c340", borderColor: "#80c340", marginRight: "10px" }}
          onClick={handleClose}
          disabled={submitting}
        >
          close
        </Button>
        <Button
          type="button"
          variant="contained"
          sx={{ width: 80 }}
          style={{ background: "#80c340", borderColor: "#80c340", marginRight: "10px" }}
          onClick={handleSave}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={22} /> : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
